import { Printer, Trash } from "lucide-react"
import { Button } from "@components/common/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@components/common/card"
import {
  ALL_MAIN_BOQ_ENDPOINT,
  DELETE_ESTIMATE_BOQ_ENDPOINT,
  FINANCIAL_SERVER_URL,
  GET_ESTIMATE_BOQ_ENDPOINT,
} from "@pma/utils/api-routes"
import axios from "axios"
import axiosServices from "@pma/utils/axios"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import useTranslator from "@pma/hooks/use-translator"
import { cn, getUserFromId, getFinancialErrorMessage } from "@pma/utils/helpers"
import { useAuth } from "@pma/stores/auth-store"
import { Link, useNavigate, useParams } from "react-router-dom"
import { FINANCIAL_BOQ_ROUTE, PROJECTS_PAGE_ROUTE } from "@pma/utils/page-routes"
import { format, formatDistance } from "date-fns"
import { he } from "date-fns/locale"
import { Tooltip, TooltipContent, TooltipTrigger } from "../common/tooltip"
import { toast } from "@pma/hooks/use-toast"
import PmsLoader from "@components/common/loader"
import CreateMainBoQ from "./create-main-boq"
import EstimateBoQTable from "./estimate-boq-table"
import { useContext, useEffect, useState } from "react"
import { UserRoleContext } from "@pma/contexts/UserRoleContext"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../common/alert-dialog"
import EstimateBillOfQuantitiesPrint from "./estimate-bill-of-quantities-print"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/common/tabs"
import EstimateVsActual from "./estimate-vs-actual"

interface BoQDetailsProps {
  boqId: string
  sm?: boolean
  closeDialog?: CallableFunction
}

type BoQItem = {
  id: string
  number: string
  description: string
  units: string
  quantity: number
  price: number
  amount: number
  is_item: boolean
  sum: string
  created_at: string
}

export function EstimateBoQDetails({ boqId, sm, closeDialog }: BoQDetailsProps) {
  const { authConfig, setProjectFinancial } = useAuth()
  const queryClient = useQueryClient()

  const { translate, locale } = useTranslator()

  const { userRoleDetails } = useContext(UserRoleContext)

  const navigate = useNavigate()
  const params = useParams()

  const isHebrew = locale === "he"

  const projectId = params?.projectId

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const organizationUsers = authConfig?.organizationUsers

  const [estimateBillOfQuantitiesPrintDialog, setEstimateBillOfQuantitiesPrintDialog] = useState(false)
  const [hasLinkedMainBoq, setHasLinkedMainBoq] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function checkMainBoq() {
      try {
        const orgId = authConfig?.last_organization_id
        if (!orgId || !boqId) return
        const res = await axiosServices.post(FINANCIAL_SERVER_URL + ALL_MAIN_BOQ_ENDPOINT, {
          organization_id: orgId,
          ...(projectId ? { project_id: Number(projectId) } : {}),
          show_deleted: false,
        })
        const boqs = Array.isArray(res.data) ? res.data : res.data?.items || []
        const hasMatch = boqs.some(
          (b: any) => String(b?.estimate_boq_id) === String(boqId),
        )
        if (!cancelled) setHasLinkedMainBoq(hasMatch)
      } catch {
        // silently fail - button just won't show
      }
    }
    checkMainBoq()
    return () => { cancelled = true }
  }, [boqId, projectId, authConfig?.last_organization_id])

  const getEstimateBoQDetails = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + GET_ESTIMATE_BOQ_ENDPOINT,
      {
        boq_estimate_id: boqId,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const {
    data: estimateBoQDetails,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["estimate-boq", boqId],
    queryFn: getEstimateBoQDetails,
  })

  const boq = estimateBoQDetails?.boq

  const boqItems: BoQItem[] = estimateBoQDetails?.boq_items || []

  const deleteBoQEstimate = async (boQId: string) => {
    const response = await axios.delete(
      FINANCIAL_SERVER_URL + DELETE_ESTIMATE_BOQ_ENDPOINT + `/${boQId}`,
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const handleDeleteBoQMutation = useMutation({
    mutationKey: ["delete-boq-estimate"],
    mutationFn: deleteBoQEstimate,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("BoQ Deleted Successfully"),
        variant: "success",
      })
      refetch()

      setProjectFinancial({
        projectId: projectId,
        estimate: undefined,
        main: undefined,
      })
      queryClient.invalidateQueries({ queryKey: ["main-boq"] })

      closeDialog && closeDialog()
      if (projectId) {
        return navigate(PROJECTS_PAGE_ROUTE + "/" + projectId)
      } else {
        navigate(FINANCIAL_BOQ_ROUTE)
      }
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "delete estimate", translate),
        variant: "destructive",
      })
    },
  })

  return (
    <div
      className={cn(
        "flex h-full w-full flex-col gap-2 overflow-hidden",
        sm ? "gap-1" : "gap-4 sm:gap-4 md:gap-6",
        isHebrew ? "rtl" : "ltr",
      )}
    >
      {!sm && (
        <Card className='w-full flex-shrink-0 border shadow-sm'>
          <CardHeader className='w-full px-3 py-3 sm:px-4 sm:py-4 md:px-6 md:py-4'>
            <div className='flex w-full flex-col gap-3 sm:gap-4'>
              <div className='w-full min-w-0'>
                <CardTitle className='truncate text-base sm:text-lg md:text-xl'>
                  {boq?.name || "---"}
                </CardTitle>
                <CardDescription className='mt-2 w-full sm:mt-3'>
                  <div className='flex flex-col gap-2 text-xs sm:gap-3 sm:text-sm'>
                    {/* Project Row */}
                    <div className='flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center'>
                      <span className='font-medium text-foreground'>{translate("Project")}:</span>
                      <Link to={`${PROJECTS_PAGE_ROUTE}/${boq?.project_id}`} className='min-w-0'>
                        <Button
                          variant='link'
                          className='h-auto p-0 text-xs font-medium sm:text-sm'
                          size='sm'
                        >
                          {`#${boq?.project_id || "---"}`}
                        </Button>
                      </Link>
                    </div>

                    {/* Planner Row */}
                    <div className='flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center'>
                      <span className='font-medium text-foreground'>{translate("Planner")}:</span>
                      <span className='truncate font-medium'>
                        {getUserFromId(boq?.designer_id, organizationUsers)?.name || "---"}
                      </span>
                    </div>

                    {/* Created Row */}
                    <div className='flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center'>
                      <span className='font-medium text-foreground'>{translate("Created")}:</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className='cursor-pointer font-medium hover:underline'>
                            {boq?.created_at
                              ? formatDistance(boq?.created_at, new Date(), {
                                  locale: isHebrew ? he : undefined,
                                  addSuffix: true,
                                })
                              : "---"}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {boq?.created_at && format(boq?.created_at, "dd/MM/yyyy")}
                        </TooltipContent>
                      </Tooltip>
                    </div>

                    {/* Action Buttons Row */}
                    <div className='flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
                      <AlertDialog>
                        <AlertDialogTrigger>
                          <Button
                            rightSection={<Trash size={16} />}
                            size='sm'
                            variant='ghost'
                            className='justify-start text-xs font-medium text-destructive hover:bg-destructive/10 hover:text-destructive/90'
                          >
                            {translate("Delete")}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{translate("Are you sure")}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {translate(
                                "Deleting estimate BoQ will result in loss of analytical information",
                              )}
                              .
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{translate("Cancel")}</AlertDialogCancel>
                            <AlertDialogAction
                              className='bg-destructive'
                              disabled={handleDeleteBoQMutation?.isPending}
                              onClick={() => handleDeleteBoQMutation.mutate(boqId)}
                            >
                              {translate("Continue")}{" "}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>

                      <div className='flex gap-2'>
                        <Button
                          variant={"outline"}
                          size={"sm"}
                          onClick={() => setEstimateBillOfQuantitiesPrintDialog(true)}
                        >
                          <Printer className={`mx-1 h-4 w-4`} />
                          <span className='hidden sm:inline'>{translate("Print")}</span>
                        </Button>
                        {userRoleDetails?.supervisor || userRoleDetails?.fin_manager ? (
                          <div className='w-full sm:w-auto'>
                            <CreateMainBoQ
                              revalidate={refetch}
                              estimateBoQId={boqId}
                              projectId={projectId}
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      )}

      {/* Tabbed content: Estimate Items + Comparison */}
      <Tabs defaultValue="estimate-items" className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
        {hasLinkedMainBoq ? (
          <TabsList className="flex-shrink-0 px-3 sm:px-4 md:px-6">
            <TabsTrigger value="estimate-items">
              {isHebrew ? "פריטי אומדן" : "Estimate Items"}
            </TabsTrigger>
            <TabsTrigger value="estimate-vs-actual">
              {isHebrew ? "אומדן מול ביצוע" : "Estimate vs Actual"}
            </TabsTrigger>
          </TabsList>
        ) : (
          <div className="flex-shrink-0 px-3 py-3 sm:px-4 sm:py-4 md:px-6">
            <h3 className="text-sm font-semibold sm:text-base">
              {sm && boq?.name && <span className="block truncate">{boq.name}</span>}
              <span>{translate("Estimate Items")}</span>
            </h3>
          </div>
        )}

        <TabsContent value="estimate-items" className="min-h-0 flex-1 overflow-hidden mt-0">
          <Card className="flex min-h-0 w-full flex-1 flex-col overflow-hidden border-0 shadow-none">
            <CardContent className="min-h-0 flex-1 overflow-auto p-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <PmsLoader />
                </div>
              ) : (
                <>
                  {boqItems?.length > 0 ? (
                    <div className="w-full overflow-x-auto">
                      <EstimateBoQTable data={boqItems} boqId={boqId} revalidate={refetch} />
                    </div>
                  ) : (
                    <div className="flex w-full items-center justify-center px-4 py-8">
                      <p className="text-xs font-medium text-muted-foreground sm:text-sm">
                        {translate("No items found")}
                      </p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {hasLinkedMainBoq && (
          <TabsContent value="estimate-vs-actual" className="min-h-0 flex-1 overflow-auto mt-0">
            <EstimateVsActual estimateBoqId={boqId} projectId={projectId} />
          </TabsContent>
        )}
      </Tabs>

      {estimateBillOfQuantitiesPrintDialog && (
        <EstimateBillOfQuantitiesPrint
          data={estimateBoQDetails.boq_items}
          estimateBillOfQuantitiesPrintDialog={estimateBillOfQuantitiesPrintDialog}
          setEstimateBillOfQuantitiesPrintDialog={setEstimateBillOfQuantitiesPrintDialog}
        />
      )}
    </div>
  )
}
